import {Button, Checkbox, Col, Divider, Modal, Row, Typography} from "antd";
import {capitalize} from "lodash";
import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useGetAssetBiodiversityDataSourcesQuery} from "../../../../redux/api/asset";
import {AssetsTableColumn} from "../../../../types/internal/tables/assetsTable";
import {getTranslationAssetTableColumnKey} from "../../../../utils/translation";
import {getColumnsBySection} from "../../../assets/AssetsTable/AssetsTableColumnsSelection";
import {useGetHazardTypeClassesQuery} from "../../../../redux/api/hazardTypes";
import {useGetAlertParametersTypeQuery} from "../../../../redux/api/alertParameters";

interface ColumnSectionProps {
  title: 'position' | 'buildingDetails' | 'realEstateData' | 'event',
  columns: string[],
  addColumns: (columns: string | string[]) => void,
  removeColumns: (columns: string | string[]) => void,
}
function ColumnsSection({
  title,
  columns,
  addColumns,
  removeColumns,
}: ColumnSectionProps) {
  const {t} = useTranslation();
  const {
    data: biodiversitySources,
  } = useGetAssetBiodiversityDataSourcesQuery();
  const allBiodiversitySources = (biodiversitySources ?? [])?.map(el => el.value);
  const handleChange = (key: AssetsTableColumn, isChecked: boolean) => {
    if (key === 'overall_land_coverage') {
      if (isChecked) {
        addColumns(allBiodiversitySources);
      } else {
        removeColumns(allBiodiversitySources);
      }
    } else {
      if (isChecked) {
        addColumns(key);
      } else {
        removeColumns(key);
      }
    }
  };

  return <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
    <Typography.Title level={4} style={{margin: 0}}>{capitalize(t(`misc.${title}`))}</Typography.Title>
    <Row style={{marginLeft: '1rem'}} gutter={[12, 12]}>
      {getColumnsBySection(title).map(el => (
        <Col
          key={el}
          span={12}
        >
          <Checkbox
            checked={columns.includes(el)}
            onChange={e => handleChange(el, e.target.checked)}
          >
            {capitalize(t(getTranslationAssetTableColumnKey(el)))}
          </Checkbox>
        </Col>
      ))}
      {title === 'buildingDetails' && (
        <Col
          span={12}
        >
          <Checkbox
            checked={allBiodiversitySources.every(source => columns.includes(source))}
            onChange={e => handleChange('overall_land_coverage', e.target.checked)}
          >
            {capitalize(t('assets.landCoveragePercentage'))}
          </Checkbox>
        </Col>
      )}
    </Row>
  </div>;
}

interface HazardSectionProps {
  columns: string[],
  addColumns: (columns: string | string[]) => void,
  removeColumns: (columns: string | string[]) => void,
}
function HazardSection({ columns, addColumns, removeColumns }: HazardSectionProps) {
  const {
    data: hazardTypes,
  } = useGetHazardTypeClassesQuery();

  const {t} = useTranslation();
  const hazardsColumns = hazardTypes?.map(el => el.code) || [];
  const isToRemoveHazardsColumns = columns.some(el => hazardsColumns.includes(el));

  const handleChange = (key: AssetsTableColumn, isChecked: boolean) => {
    if (isChecked) {
      addColumns(key);
    } else {
      removeColumns(key);
    }
  };

  return <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
    <Row justify={'space-between'}>
      <Col>
        <Typography.Title level={4} style={{margin: 0}}>
          {capitalize(t('hazards.hazard', {count: 2}))}
        </Typography.Title>
      </Col>
      <Col>
        <Button
          onClick={() => {
            if (isToRemoveHazardsColumns) {
              removeColumns(hazardsColumns);
            } else {
              addColumns(hazardsColumns);
            }
          }}
        >
          {isToRemoveHazardsColumns && capitalize(t('actions.deselectAll'))}
          {!isToRemoveHazardsColumns && capitalize(t('actions.selectAll'))}
        </Button>
      </Col>
    </Row>
    <Row style={{marginLeft: '1rem'}} gutter={[12, 12]}>
      {!!hazardTypes && hazardTypes?.map(ht => (
        <Col
          key={ht.code}
          span={12}
        >
          <Checkbox
            checked={columns.includes(ht.code)}
            onChange={e => handleChange(ht.code, e.target.checked)}
          >
            {capitalize(ht.name)}
          </Checkbox>
        </Col>
      ))}
    </Row>
  </div>;
}

interface AlertParametersSectionProps {
  columns: string[],
  addColumns: (columns: string | string[]) => void,
  removeColumns: (columns: string | string[]) => void,
}
function AlertParametersSection({ columns, addColumns, removeColumns }: AlertParametersSectionProps) {
  const {data: alertParametersTypeSources} = useGetAlertParametersTypeQuery({paginate: false})

  const {t} = useTranslation();

  const handleChange = useCallback((key: AssetsTableColumn, isChecked: boolean) => {
    if (isChecked) {
      addColumns(key);
    } else {
      removeColumns(key);
    }
  }, [addColumns, removeColumns])

  return <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
    <Row justify={'space-between'}>
      <Col>
        <Typography.Title level={4} style={{margin: 0}}>
          {capitalize(t('assets.alertParameters', {count: 2}))}
        </Typography.Title>
      </Col>
    </Row>
    <Row style={{marginLeft: '1rem'}} gutter={[12, 12]}>
      {!!alertParametersTypeSources && alertParametersTypeSources?.map(param => (
        <Col
          key={param.code}
          span={12}
        >
          <Checkbox
            checked={columns.includes(param.code)}
            onChange={e => handleChange(param.code, e.target.checked)}
          >
            {capitalize(param.label)}
          </Checkbox>
        </Col>
      ))}
    </Row>
  </div>;
}

interface ColumnSelectionCreationAreasProps {
  columns: string[],
  addColumns: (column: string | string[]) => void,
  removeColumns: (column: string | string[]) => void,
  resetColumns: () => void,
}
export default function ColumnSelectionCreationAreas({
  columns,
  addColumns,
  removeColumns,
  resetColumns
}: ColumnSelectionCreationAreasProps) {
  const {t} = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  return (
    <>
      <Button
        onClick={() => setIsModalVisible(true)}
      >
        {capitalize(t('table.selectColumns'))}
      </Button>
      <Modal
        title={capitalize(t('table.selectColumns'))}
        open={isModalVisible}
        footer={
          <Row justify={'end'} style={{marginTop: '2rem'}}>
            <Col>
              <Button
                danger
                onClick={() => resetColumns()}
              >
                {capitalize(t('form.reset'))}
              </Button>
            </Col>
          </Row>
        }
        onCancel={() => setIsModalVisible(false)}
        closeIcon={false}
      >
        <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
          <Divider style={{margin: 0}}/>
          <ColumnsSection
            title={'position'}
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}
          />
          <Divider style={{margin: 0}}/>
          <ColumnsSection
            title={'buildingDetails'}
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}
          />
          <Divider style={{margin: 0}}/>
          <ColumnsSection
            title={'realEstateData'}
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}
          />
          <Divider style={{margin: 0}}/>
          <ColumnsSection
            title={'event'}
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}/>
          <Divider style={{margin: 0}}/>
          <HazardSection
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}
          />
          <Divider style={{margin: 0}}/>
          <AlertParametersSection
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}/>
        </div>
      </Modal>
    </>
  );
}